/**
 * Version history for one app. Lists deployments grouped by env (live
 * first, then preview). Manager-only actions — every button names the
 * environment it acts on, since "Promote to live" sits in the preview
 * list and two different rollbacks live one card apart:
 *   - "Promote to live" on the latest preview row → POST /promote
 *   - "Roll back live" on each prior live row → POST /restore-version
 *   - "Roll back preview" on each prior preview row → POST /restore-preview-version
 *   - "Reset preview database from live" → POST /reset-preview
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, apiMessage } from '../api/client.js';
import type { DeploymentRow } from '../api/types.js';
import { AppTabs } from '../components/AppTabs.js';
import { ManagerOnlyPage } from '../components/ManagerOnly.js';
import { IconRefresh, IconRocket } from '../components/Icons.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Banner, Button, Card, CardHeader, PageHeader, PageSkeleton } from '../components/ui.js';
import { relTime } from '../lib/format.js';
import { useAppPageWith } from '../lib/useAppPage.js';
import { runAction, type ActionMessage } from '../lib/runAction.js';

/**
 * Rollbacks overwhelmingly target the last few versions, so the first
 * page shows 10 per env — target plus context, no scrolling. Anything
 * deeper is history-skimming by summary; "Show older" pages 25 at a
 * time (agent-driven preview histories run to hundreds of versions).
 */
const INITIAL_VERSIONS = 10;
const OLDER_PAGE_SIZE = 25;

interface EnvPage {
  deployments: DeploymentRow[];
  total: number;
}

function deploymentsUrl(
  slug: string,
  env: 'live' | 'preview',
  limit: number,
  beforeVersion?: number,
): string {
  const cursor = beforeVersion !== undefined ? `&before_version=${beforeVersion}` : '';
  return `/api/apps/${encodeURIComponent(slug)}/deployments?env=${env}&limit=${limit}${cursor}`;
}

export function Versions() {
  const { slug } = useParams();
  const { app, payload, loading, error, reload } = useAppPageWith(slug, (s) =>
    Promise.all([
      api.get<EnvPage>(deploymentsUrl(s, 'live', INITIAL_VERSIONS)),
      api.get<EnvPage>(deploymentsUrl(s, 'preview', INITIAL_VERSIONS)),
    ]).then(([live, preview]) => ({ live, preview })),
  );
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
  // Pages appended via "Show older". Reset whenever the base payload
  // refreshes — an action reloaded the first page, so appended rows
  // may be stale or duplicated.
  const [older, setOlder] = useState<{ live: DeploymentRow[]; preview: DeploymentRow[] }>({
    live: [],
    preview: [],
  });
  const [loadingOlder, setLoadingOlder] = useState<'live' | 'preview' | null>(null);
  useEffect(() => {
    setOlder({ live: [], preview: [] });
  }, [payload]);

  if (loading) return <PageSkeleton rows={5} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app || !payload) return null;

  // Server returns newest-first per env, so the active row is always first.
  const liveDeploys = [...payload.live.deployments, ...older.live];
  const previewDeploys = [...payload.preview.deployments, ...older.preview];
  const liveTotal = payload.live.total;
  const previewTotal = payload.preview.total;
  const activeLive = liveDeploys.find((d) => d.status === 'active') ?? null;
  const activeLiveId = activeLive?.id ?? null;
  const latestPreview = previewDeploys[0];
  // The latest preview is only promotable when it's strictly newer than
  // what's live. If a previous promote already shipped it, the rows
  // exist but there's nothing new to push.
  const hasUnpromotedPreview =
    !!latestPreview &&
    (!activeLive || (activeLive.source_preview_version ?? 0) < latestPreview.version);

  if (!app.is_manager) {
    return <ManagerOnlyPage app={app} title="Versions" description="Deploy and publish history." />;
  }

  async function promotePreview(version: number) {
    if (!slug) return;
    if (
      !confirm(`Promote preview v${version} to live? Everyone using the app sees the new version.`)
    )
      return;
    await runAction({
      do: () =>
        api.post<{ version: number; status: string }>(
          `/api/apps/${encodeURIComponent(slug)}/promote`,
        ),
      onSuccess: (res) =>
        res.status === 'active'
          ? `Promoted v${version} to live.`
          : `Promote completed with status "${res.status}".`,
      errorPrefix: 'Promote failed',
      setBusy: (busy) => setPendingAction(busy ? `promote-${version}` : null),
      setMessage: setActionMessage,
      reload,
    });
  }

  async function resetPreview() {
    if (!slug) return;
    if (
      !confirm(
        'Replace the preview database with a copy of live? Any preview-only data and schema changes will be lost.',
      )
    )
      return;
    await runAction({
      do: () =>
        api.post<{
          ok: boolean;
          tablesCopied: string[];
          rowsCopied: number;
        }>(`/api/apps/${encodeURIComponent(slug)}/reset-preview`),
      onSuccess: (res) =>
        `Preview reset: ${res.tablesCopied.length} table(s), ${res.rowsCopied} row(s) copied from live.`,
      errorPrefix: 'Reset failed',
      setBusy: (busy) => setPendingAction(busy ? 'reset' : null),
      setMessage: setActionMessage,
      reload,
    });
  }

  async function rollbackVersion(version: number, sourcePreviewVersion: number | null) {
    if (!slug) return;
    const label = `v${sourcePreviewVersion ?? version}`;
    if (
      !confirm(
        `Roll live back to ${label}? A new live deployment will be created from the archived bundle.`,
      )
    )
      return;
    await runAction({
      do: () =>
        api.post<{
          version: number;
          status: string;
          source_preview_version?: number | null;
        }>(`/api/apps/${encodeURIComponent(slug)}/restore-version`, { version }),
      onSuccess: (res) => {
        const newLabel = `v${res.source_preview_version ?? res.version}`;
        return `Rolled back to ${label} — live is now on ${newLabel}.`;
      },
      errorPrefix: 'Rollback failed',
      setBusy: (busy) => setPendingAction(busy ? `rollback-${version}` : null),
      setMessage: setActionMessage,
      reload,
    });
  }

  async function rollbackPreviewVersion(version: number) {
    if (!slug) return;
    if (
      !confirm(
        `Roll preview back to v${version}? A new preview version is created with v${version}'s code, ` +
          `and preview database migrations applied after it are reversed. Live is not affected.`,
      )
    )
      return;
    await runAction({
      do: () =>
        api.post<{ version: number; restoredFromVersion: number; revertedMigrations: number }>(
          `/api/apps/${encodeURIComponent(slug)}/restore-preview-version`,
          { version },
        ),
      onSuccess: (res) =>
        `Preview rolled back to v${res.restoredFromVersion} — now serving as preview v${res.version}` +
        (res.revertedMigrations > 0 ? ` (${res.revertedMigrations} migration(s) reverted).` : '.'),
      errorPrefix: 'Preview rollback failed',
      setBusy: (busy) => setPendingAction(busy ? `preview-rollback-${version}` : null),
      setMessage: setActionMessage,
      reload,
    });
  }

  async function showOlder(envName: 'live' | 'preview') {
    if (!slug || loadingOlder !== null) return;
    const rows = envName === 'live' ? liveDeploys : previewDeploys;
    const oldest = rows[rows.length - 1];
    if (!oldest) return;
    setLoadingOlder(envName);
    try {
      const page = await api.get<EnvPage>(
        deploymentsUrl(slug, envName, OLDER_PAGE_SIZE, oldest.version),
      );
      setOlder((prev) => ({ ...prev, [envName]: [...prev[envName], ...page.deployments] }));
    } catch (err) {
      setActionMessage({ tone: 'error', text: `Couldn't load older versions: ${apiMessage(err)}` });
    } finally {
      setLoadingOlder(null);
    }
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        title="Versions"
        description="Every deploy and publish, newest first. Promote the latest preview to live, or roll live or preview back to a prior version."
      />

      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0 space-y-5">
          {actionMessage && (
            <Banner tone={actionMessage.tone} onDismiss={() => setActionMessage(null)}>
              {actionMessage.text}
            </Banner>
          )}

          <Card>
            <CardHeader
              title={
                <span className="inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-ok" />
                  Live
                </span>
              }
              description={`What everyone using the app sees · ${liveTotal} version${liveTotal === 1 ? '' : 's'}`}
            />
            {liveDeploys.length === 0 ? (
              <p className="px-5 py-5 text-muted text-sm">No live versions yet.</p>
            ) : (
              <ul className="divide-y divide-line">
                {liveDeploys.map((d) => (
                  <li key={d.id} className="px-5 py-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2">
                        <span className="font-semibold text-ink tabular">
                          v{d.source_preview_version ?? d.version}
                        </span>
                        <StatusBadge status={d.status} />
                        {d.id === activeLiveId && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-ok-soft text-ok font-medium ring-1 ring-inset ring-ok/20">
                            live
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="text-xs text-muted">{relTime(d.created_at)}</span>
                        {d.id !== activeLiveId && d.status !== 'failed' && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              void rollbackVersion(d.version, d.source_preview_version)
                            }
                            loading={pendingAction === `rollback-${d.version}`}
                            disabled={pendingAction !== null}
                            title="Make this version live again. Everyone using the app switches to it."
                          >
                            Roll back live
                          </Button>
                        )}
                      </span>
                    </div>
                    {d.ai_summary && <p className="text-muted text-sm mt-1.5">{d.ai_summary}</p>}
                  </li>
                ))}
              </ul>
            )}
            {liveDeploys.length < liveTotal && (
              <div className="px-5 py-3 border-t border-line">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void showOlder('live')}
                  loading={loadingOlder === 'live'}
                  disabled={loadingOlder !== null}
                >
                  Show older versions ({liveTotal - liveDeploys.length} more)
                </Button>
              </div>
            )}
          </Card>

          <Card>
            <CardHeader
              title={
                <span className="inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-warn" />
                  Preview
                </span>
              }
              description={`Your work-in-progress copy at preview-${app.slug} · ${previewTotal} version${previewTotal === 1 ? '' : 's'}`}
              actions={
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void resetPreview()}
                  loading={pendingAction === 'reset'}
                  disabled={pendingAction !== null}
                  title="Replace the preview database with a copy of live's schema and data."
                >
                  <IconRefresh size={13} />
                  Reset preview database from live
                </Button>
              }
            />
            {previewDeploys.length === 0 ? (
              <p className="px-5 py-5 text-muted text-sm">No preview versions yet.</p>
            ) : (
              <ul className="divide-y divide-line">
                {previewDeploys.map((d) => {
                  // Only the latest preview is promotable — kin_promote always
                  // takes whatever was last sent. Older preview rows are
                  // history. And if the latest preview already shipped (live's
                  // source_preview_version matches), there's nothing to promote.
                  const isLatest = d.id === latestPreview?.id;
                  const canPromote = isLatest && d.status !== 'failed' && hasUnpromotedPreview;
                  return (
                    <li key={d.id} className="px-5 py-3 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2">
                          <span className="font-semibold text-ink tabular">v{d.version}</span>
                          <StatusBadge status={d.status} />
                          {isLatest && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-warn-soft text-warn font-medium ring-1 ring-inset ring-warn/20">
                              latest
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-3">
                          <span className="text-xs text-muted">{relTime(d.created_at)}</span>
                          {!isLatest && d.status !== 'failed' && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => void rollbackPreviewVersion(d.version)}
                              loading={pendingAction === `preview-rollback-${d.version}`}
                              disabled={pendingAction !== null}
                              title="Make this version the current preview. Live is not affected."
                            >
                              Roll back preview
                            </Button>
                          )}
                          {canPromote && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => void promotePreview(d.version)}
                              loading={pendingAction === `promote-${d.version}`}
                              disabled={pendingAction !== null}
                              title="Publish this preview version to live for everyone."
                            >
                              <IconRocket size={13} />
                              Promote to live
                            </Button>
                          )}
                        </span>
                      </div>
                      {d.ai_summary && <p className="text-muted text-sm mt-1.5">{d.ai_summary}</p>}
                    </li>
                  );
                })}
              </ul>
            )}
            {previewDeploys.length < previewTotal && (
              <div className="px-5 py-3 border-t border-line">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void showOlder('preview')}
                  loading={loadingOlder === 'preview'}
                  disabled={loadingOlder !== null}
                >
                  Show older versions ({previewTotal - previewDeploys.length} more)
                </Button>
              </div>
            )}
          </Card>
        </main>
      </div>
    </div>
  );
}
